import { useCallback } from 'react';

import { OasMemoryError, OasError } from 'core/atoms/errors';

import { gql, useMutation } from 'core/dna/types/apollo';
import {
  CreateSubstituteJob,
  CreateSubstituteJobVariables,
} from 'core/dna/types/generated/CreateSubstituteJob';

import { GET_SUBSTITUTE_JOBS_QUERY } from 'modules/substitute/memory/apollo/substitute-jobs/remote';

export const CREATE_SUBSTITUTE_JOB_MUTATION = gql`
  mutation CreateSubstituteJob($input: CreateSubstituteJobInput) {
    createSubstituteJob(input: $input) {
      id
      description
      internalTitle
      deadline {
        formatted
      }
      state
    }
  }
`;

const TITLE = 'use-create-substitute-job';

export const useCreateSubstituteJob = () => {
  const [createSubstituteJob] = useMutation<
    CreateSubstituteJob,
    CreateSubstituteJobVariables
  >(CREATE_SUBSTITUTE_JOB_MUTATION);

  const func = useCallback(
    async (description: string, internalTitle: string, deadline: string) => {
      try {
        const result = await createSubstituteJob({
          variables: {
            input: {
              description,
              internalTitle,
              deadline,
            },
          },
          refetchQueries: [
            {
              query: GET_SUBSTITUTE_JOBS_QUERY,
            },
          ],
        });

        if (result.errors) {
          throw new OasMemoryError(JSON.stringify(result.errors), {
            title: TITLE,
          });
        }
        return result.data?.createSubstituteJob ?? undefined;
      } catch (e) {
        if (e instanceof OasError) {
          throw e;
        } else {
          throw OasMemoryError.fromError(e, {
            title: TITLE,
          });
        }
      }
    },
    [createSubstituteJob],
  );

  return {
    createSubstituteJob: func,
  };
};
